// Microbe RTS — PvP transport: one WebSocket per player to the dumb relay.
//
// connectMatch(code, {engine, url?}) opens `${url}/join/:code?engine=…` and demultiplexes the relay's
// JSON frames into typed callbacks (onStart/onInput/onSum/onDesync/onPeerGone/onEngineMismatch/onError),
// plus send(frame) + close(). The lockstep scheduler (net/lockstep.ts) drives this via net/pvp.ts.
//
// The Transport interface is the seam the fake in-process relay (net/lockstep-e2e.ts) also implements,
// so the exact same session code runs headless with no network. `seed`/`side` latch on 'start'.

import type { Side } from '../types.js';
import type { ClientMsg, Engine, InputMsg, ServerMsg, SumMsg, DesyncMsg } from './protocol.js';
import { RELAY_URL } from './protocol.js';

/** The seam between the network and the lockstep session. Implemented by WsTransport (real) and the
 *  in-process fake relay (tests). Callbacks are single-slot (last registration wins). */
export interface Transport {
  /** Shared match seed, latched once 'start' arrives (null before). */
  readonly seed: number | null;
  /** This client's side, latched once 'start' arrives (null before). */
  readonly side: Side | null;
  onStart(cb: (seed: number, side: Side) => void): void;
  onInput(cb: (msg: InputMsg) => void): void;
  onSum(cb: (msg: SumMsg) => void): void;
  onDesync(cb: (msg: DesyncMsg) => void): void;
  onPeerGone(cb: () => void): void;
  onEngineMismatch(cb: () => void): void;
  onError(cb: (err: unknown) => void): void;
  /** Send a client frame (serialized + shipped to the relay, which forwards to the peer). */
  send(msg: ClientMsg): void;
  close(): void;
}

export interface ConnectOpts {
  engine: Engine;
  /** Override the relay origin (http(s):// or ws(s)://). Defaults to RELAY_URL. */
  url?: string;
}

/** Rewrite an http(s) origin to ws(s); leave an already-ws(s) origin untouched. */
function toWsOrigin(base: string): string {
  if (base.startsWith('https://')) return 'wss://' + base.slice('https://'.length);
  if (base.startsWith('http://')) return 'ws://' + base.slice('http://'.length);
  return base;
}

/** A short, unambiguous match code for the "create match" flow (no I/O/0/1 to avoid misreads). */
export function makeCode(len = 4): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  let out = '';
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint8Array(len);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < len; i++) out += alphabet[buf[i] % alphabet.length];
  } else {
    for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/** connectMatch: open the WS and return a live Transport. "Create match" = pick a code (makeCode),
 *  show it, connectMatch(code) (first joiner = side 'you'). "Join match" = connectMatch(entered code). */
export function connectMatch(code: string, opts: ConnectOpts): Transport {
  const base = (opts.url ?? RELAY_URL).replace(/\/+$/, '');
  const origin = toWsOrigin(base);
  const url = `${origin}/join/${encodeURIComponent(code)}?engine=${encodeURIComponent(opts.engine)}`;
  return new WsTransport(url);
}

class WsTransport implements Transport {
  private ws: WebSocket;
  private _seed: number | null = null;
  private _side: Side | null = null;

  private hStart: ((seed: number, side: Side) => void) | null = null;
  private hInput: ((msg: InputMsg) => void) | null = null;
  private hSum: ((msg: SumMsg) => void) | null = null;
  private hDesync: ((msg: DesyncMsg) => void) | null = null;
  private hPeerGone: (() => void) | null = null;
  private hMismatch: (() => void) | null = null;
  private hError: ((err: unknown) => void) | null = null;

  constructor(url: string) {
    this.ws = new WebSocket(url);
    this.ws.addEventListener('message', (ev: MessageEvent) => this.onMessage(ev));
    this.ws.addEventListener('error', (ev) => this.hError?.(ev));
    this.ws.addEventListener('close', () => {
      // A close without a prior 'peer_gone' is a transport error from this client's POV.
      this.hError?.(new Error('socket closed'));
    });
  }

  get seed(): number | null {
    return this._seed;
  }
  get side(): Side | null {
    return this._side;
  }

  private onMessage(ev: MessageEvent): void {
    let msg: ServerMsg;
    try {
      msg = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data)) as ServerMsg;
    } catch (err) {
      this.hError?.(err);
      return;
    }
    switch (msg.t) {
      case 'start':
        this._seed = msg.seed;
        this._side = msg.side;
        this.hStart?.(msg.seed, msg.side);
        break;
      case 'input':
        this.hInput?.(msg);
        break;
      case 'sum':
        this.hSum?.(msg);
        break;
      case 'desync':
        this.hDesync?.(msg);
        break;
      case 'peer_gone':
        this.hPeerGone?.();
        break;
      case 'engine_mismatch':
        this.hMismatch?.();
        break;
    }
  }

  onStart(cb: (seed: number, side: Side) => void): void {
    this.hStart = cb;
    if (this._seed != null && this._side != null) cb(this._seed, this._side);
  }
  onInput(cb: (msg: InputMsg) => void): void {
    this.hInput = cb;
  }
  onSum(cb: (msg: SumMsg) => void): void {
    this.hSum = cb;
  }
  onDesync(cb: (msg: DesyncMsg) => void): void {
    this.hDesync = cb;
  }
  onPeerGone(cb: () => void): void {
    this.hPeerGone = cb;
  }
  onEngineMismatch(cb: () => void): void {
    this.hMismatch = cb;
  }
  onError(cb: (err: unknown) => void): void {
    this.hError = cb;
  }

  send(msg: ClientMsg): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }
  close(): void {
    try {
      this.ws.close();
    } catch {
      /* already closing/closed */
    }
  }
}
